import React from "react";
import { Paper, Button, Box } from "@mui/material";
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Person as LoginIcon,
  ExitToApp as LogoutIcon,
  DeleteSweep as ClearIcon,
} from "@mui/icons-material";

interface MenuDropUpProps {
  open: boolean;
  onClose: () => void;
  onSearchClick: () => void;
  onLocationEditionClick: () => void;
  onCraneSelectionClick: () => void;
  onLogoutClick: () => void;
  onClearLocationClick?: () => void;
  canAccessLocationContentEdition: boolean; // BYI Supervisor can edit content
  canManageCranes: boolean; // BYI Operator needs crane selection
  showLogoutButton: boolean; // Touch mode shows logout
  isLoggedIn: boolean; // Show logout only when logged in, hide login when logged in
  showClearLocationButton?: boolean; // Show clear location button when special location is selected
  anchorElement?: HTMLElement | null;
}

export const MenuDropUp: React.FC<MenuDropUpProps> = ({
  open,
  onClose,
  onSearchClick,
  onLocationEditionClick,
  onCraneSelectionClick,
  onLogoutClick,
  onClearLocationClick,
  canAccessLocationContentEdition,
  canManageCranes,
  showLogoutButton,
  isLoggedIn,
  showClearLocationButton = false,
  anchorElement,
}) => {
  if (!open) return null;

  // Calculate position relative to anchor (Menu button)
  const getMenuPosition = () => {
    if (anchorElement) {
      const rect = anchorElement.getBoundingClientRect();
      return {
        position: "fixed" as const,
        bottom: `${window.innerHeight - rect.top + 10}px`, // Drop UP from button
        right: `${window.innerWidth - rect.right}px`,
        zIndex: 9999,
      };
    }

    // Fallback positioning (bottom-right corner)
    return {
      position: "fixed" as const,
      bottom: "100px",
      right: "20px",
      zIndex: 9999,
    };
  };

  const handleSearchClick = () => {
    onSearchClick();
    onClose(); // Close menu after selecting search
  };

  const handleLocationClick = () => {
    onLocationEditionClick();
    onClose(); // Close menu after selecting location
  };

  const handleCraneClick = () => {
    onCraneSelectionClick();
    onClose(); // Close menu after selecting crane
  };

  const handleLogoutClick = () => {
    onLogoutClick();
    onClose(); // Close menu after logout
  };

  const handleClearLocationClick = () => {
    if (onClearLocationClick) {
      onClearLocationClick();
    }
    onClose(); // Close menu after clear location
  };

  return (
    <>
      {/* Backdrop to close menu when clicking outside */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9998,
        }}
        onClick={onClose}
      />

      {/* Menu Drop-up */}
      <Paper
        elevation={8}
        sx={{
          ...getMenuPosition(),
          minWidth: "300px",
          borderRadius: "8px",
          backgroundColor: (theme) =>
            theme.palette.billetYard.backgrounds.light,
        }}
      >
        <Box display="flex" flexDirection="column" padding="16px">
          {/* Location Button (BYI Supervisor - can edit content) */}
          {canAccessLocationContentEdition && (
            <Button
              onClick={handleLocationClick}
              variant="contained"
              startIcon={<EditIcon />}
              sx={{
                minHeight: "60px",
                fontSize: "18px",
                fontWeight: "bold",
                backgroundColor: (theme) => theme.palette.primary.main,
                color: "white",
                borderRadius: "8px",
                textTransform: "none",
                boxShadow: (theme) => theme.shadows[2],
                marginBottom: "8px",
              }}
            >
              Location
            </Button>
          )}

          {/* Login Button - Show only when user can manage cranes AND is not logged in */}
          {canManageCranes && !isLoggedIn && (
            <Button
              onClick={handleCraneClick}
              variant="contained"
              startIcon={<LoginIcon />}
              sx={{
                minHeight: "60px",
                fontSize: "18px",
                fontWeight: "bold",
                backgroundColor: (theme) => theme.palette.primary.light,
                color: "white",
                borderRadius: "8px",
                textTransform: "none",
                boxShadow: (theme) => theme.shadows[2],
                marginBottom: "8px",
              }}
            >
              Login
            </Button>
          )}

          {/* Search Button */}
          <Button
            onClick={handleSearchClick}
            variant="contained"
            startIcon={<SearchIcon />}
            sx={{
              minHeight: "60px",
              fontSize: "18px",
              fontWeight: "bold",
              backgroundColor: (theme) => theme.palette.primary.main,
              color: "white",
              borderRadius: "8px",
              textTransform: "none",
              boxShadow: (theme) => theme.shadows[2],
              marginBottom: "8px",
            }}
          >
            Search
          </Button>

          {/* Clear Location Button - Show only when special location is selected */}
          {showClearLocationButton && (
            <Button
              onClick={handleClearLocationClick}
              variant="contained"
              startIcon={<ClearIcon />}
              sx={{
                minHeight: "60px",
                fontSize: "18px",
                fontWeight: "bold",
                backgroundColor: (theme) => theme.palette.error.main,
                color: "white",
                borderRadius: "8px",
                textTransform: "none",
                boxShadow: (theme) => theme.shadows[2],
                marginBottom: "8px",
              }}
            >
              Clear
            </Button>
          )}

          {/* Logout Button - Show only when Touch mode AND user is logged in */}
          {showLogoutButton && isLoggedIn && (
            <Button
              onClick={handleLogoutClick}
              variant="contained"
              startIcon={<LogoutIcon />}
              sx={{
                minHeight: "60px",
                fontSize: "18px",
                fontWeight: "bold",
                backgroundColor: (theme) => theme.palette.error.main,
                color: "white",
                borderRadius: "8px",
                textTransform: "none",
                boxShadow: (theme) => theme.shadows[2],
                marginBottom: "8px",
              }}
            >
              Logout
            </Button>
          )}

          {/* Hide Menu Button */}
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              minHeight: "60px",
              fontSize: "18px",
              fontWeight: "bold",
              backgroundColor: (theme) => theme.palette.grey[500],
              color: "white",
              borderRadius: "8px",
              textTransform: "none",
              boxShadow: (theme) => theme.shadows[2],
            }}
          >
            Hide Menu
          </Button>
        </Box>
      </Paper>
    </>
  );
};
